/*
 * Product page gallery, every width.
 *
 * The stage is a native scroll-snap track (fingers and trackpads swipe it for
 * free); the thumbnails (a rail on desktop, a strip on phones), arrows,
 * keyboard and counter all drive that one track. Full-screen zoom is
 * PhotoSwipe 5 (MIT), imported the first time it is needed, so it costs
 * nothing on page load; on phones it zooms the photo, never the page.
 */
(() => {
  'use strict';

  const root = document.querySelector('[data-pdp-gallery-root]');
  const track = root && root.querySelector('[data-pdp-gallery]');
  if (!root || !track) return;

  const desktop = window.matchMedia('(min-width: 990px)');
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const coarse = window.matchMedia('(pointer: coarse)');
  const pdp = root.closest('.pdp');
  const slides = Array.from(track.querySelectorAll('.pdp__slide'));
  const thumbs = Array.from(root.querySelectorAll('[data-pdp-thumb]'));
  const rail = root.querySelector('[data-pdp-thumbs]');
  const prev = root.querySelector('[data-pdp-prev]');
  const next = root.querySelector('[data-pdp-next]');
  const counter = root.querySelector('[data-pdp-current]');
  const expand = root.querySelector('[data-pdp-expand]');
  const overviewButton = root.querySelector('[data-pdp-overview]');
  const total = slides.length;

  // Screen readers hear the new photo number; the visible counter is silent.
  const live = document.createElement('p');
  live.className = 'sr-only';
  live.setAttribute('aria-live', 'polite');
  root.append(live);
  let liveTimer;
  let index = 0;
  let ready = false;

  /* ── Layout measurements (the viewer fits the first screen exactly) ──── */

  const measure = () => {
    if (!pdp) return;
    const header = document.querySelector('.shopify-section-sticky-header') || document.querySelector('[data-site-header]');
    const head = header ? Math.round(header.getBoundingClientRect().height) : 88;
    // The grid is not sticky, so its document offset is stable at any scroll position.
    const start = Math.round(pdp.getBoundingClientRect().top + window.scrollY);
    pdp.style.setProperty('--pdp-head', `${head}px`);
    pdp.style.setProperty('--pdp-start', `${start}px`);
    pdp.style.setProperty('--pdp-avail', `${pdp.clientWidth}px`);
  };

  /* ── Navigation ─────────────────────────────────────────────────────── */

  const behavior = () => (reducedMotion.matches ? 'auto' : 'smooth');

  const goTo = (target, smooth = true) => {
    if (!total) return;
    const wrapped = (target + total) % total;
    // Neighbours glide; a far jump (thumbnail 2 -> 24) lands instantly
    // instead of sweeping past every photo in between.
    const near = Math.abs(wrapped - index) <= 2;
    placeholder(wrapped);
    warm(wrapped);
    track.scrollTo({ left: wrapped * track.clientWidth, behavior: smooth && near ? behavior() : 'auto' });
    setIndex(wrapped);
  };

  // Until a photo arrives, its (already small and loaded) thumbnail fills
  // the stage, so a far jump never shows an empty frame.
  const placeholder = (i) => {
    const slide = slides[i];
    const img = slide?.querySelector('img');
    if (!img || slide.hasAttribute('data-pdp-ph') || (img.complete && img.naturalWidth)) return;
    const thumb = thumbs[i]?.querySelector('img');
    const src = thumb && (thumb.currentSrc || thumb.src);
    if (!src) return;
    slide.setAttribute('data-pdp-ph', '');
    slide.style.backgroundImage = `url("${src}")`;
  };

  // Photos 2+ arrive with their files in data- attributes (product.liquid),
  // so nothing but photo 1 downloads while the page loads.
  const hydrate = (img) => {
    if (!img?.dataset.srcset) return;
    img.srcset = img.dataset.srcset;
    img.removeAttribute('data-srcset');
    if (img.dataset.src) {
      img.src = img.dataset.src;
      img.removeAttribute('data-src');
    }
  };

  const warm = (i) => {
    const w = (i + total) % total;
    const img = slides[w]?.querySelector('img');
    if (!img) return;
    if (img.loading === 'lazy') {
      // sizes="auto" is only valid on lazy images: an eager one would fall
      // back to 100vw and fetch a file twice the size it needs.
      if (/^\s*auto\s*,/.test(img.sizes)) img.sizes = img.sizes.replace(/^\s*auto\s*,\s*/, '');
      img.loading = 'eager';
    }
    hydrate(img);
    // Its thumbnail waits underneath, so a quick swipe never shows an empty frame.
    if (ready) placeholder(w);
  };

  // Desktop: originals under 1.5x the stage show nothing new in full screen.
  // (On phones even a 720px original is nearly twice the screen: zoom stays.)
  const lowRes = (i) => {
    if (!desktop.matches) return false;
    const width = parseInt(slides[i]?.dataset.zoomWidth, 10) || 0;
    return width > 0 && width < track.clientWidth * 1.5;
  };

  const railIsStrip = () => Boolean(rail) && rail.scrollWidth > rail.clientWidth + 1;

  const keepThumbInView = (thumb) => {
    if (!rail || !thumb) return;
    const strip = railIsStrip();
    const start = strip ? thumb.offsetLeft : thumb.offsetTop;
    const size = strip ? thumb.offsetWidth : thumb.offsetHeight;
    const view = strip ? rail.clientWidth : rail.clientHeight;
    const scrolled = strip ? rail.scrollLeft : rail.scrollTop;
    const pad = size * 0.6;
    const key = strip ? 'left' : 'top';
    if (start - pad < scrolled) rail.scrollTo({ [key]: Math.max(0, start - pad), behavior: behavior() });
    else if (start + size + pad > scrolled + view) rail.scrollTo({ [key]: start + size + pad - view, behavior: behavior() });
  };

  function setIndex(i) {
    if (i === index && ready) return;
    index = i;
    thumbs.forEach((thumb, t) => {
      thumb.setAttribute('aria-current', String(t === i));
      // The rail is one Tab stop; arrow keys move within it.
      thumb.tabIndex = t === i ? 0 : -1;
    });
    if (counter) counter.textContent = String(i + 1);
    root.classList.toggle('is-lowres', lowRes(i));
    if (ready) {
      clearTimeout(liveTimer);
      liveTimer = setTimeout(() => { live.textContent = `Photo ${i + 1} of ${total}`; }, 250);
    }
    keepThumbInView(thumbs[i]);
    // Photo 1 loads on its own at high priority: no stand-in, no neighbours yet.
    if (!ready) return;
    placeholder(i);
    warm(i + 1);
    // At the first photo, "previous" would wrap to the last one: skip it.
    if (i > 0) warm(i - 1);
  }

  // Any width: whatever photo is in view brings in the ones either side
  // (on phones this includes the loop's edge clones).
  let nearQueued = false;
  track.addEventListener('scroll', () => {
    if (nearQueued) return;
    nearQueued = true;
    requestAnimationFrame(() => {
      nearQueued = false;
      const width = track.clientWidth;
      if (!width) return;
      const all = track.querySelectorAll('.pdp__slide img');
      const i = Math.round(track.scrollLeft / width);
      [i, i + 1, i - 1].forEach((n) => {
        const img = all[n];
        if (!img) return;
        if (img.loading === 'lazy' && /^\s*auto\s*,/.test(img.sizes)) img.sizes = img.sizes.replace(/^\s*auto\s*,\s*/, '');
        hydrate(img);
        placeholder(n);
      });
    });
  }, { passive: true });

  let scrollQueued = false;
  track.addEventListener('scroll', () => {
    if (scrollQueued) return;
    scrollQueued = true;
    requestAnimationFrame(() => {
      scrollQueued = false;
      const width = track.clientWidth;
      if (width) setIndex(Math.round(track.scrollLeft / width));
    });
  }, { passive: true });

  thumbs.forEach((thumb, t) => {
    thumb.addEventListener('click', () => goTo(t));
    // Hovering a thumbnail starts its photo loading, so the click is instant.
    thumb.addEventListener('pointerenter', () => warm(t), { passive: true });
    thumb.addEventListener('focus', () => warm(t));
  });
  prev?.addEventListener('click', () => goTo(index - 1));
  next?.addEventListener('click', () => goTo(index + 1));

  root.addEventListener('keydown', (event) => {
    if (!desktop.matches || event.altKey || event.ctrlKey || event.metaKey) return;
    const onThumb = event.target.closest?.('[data-pdp-thumb]');
    let target = null;
    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') target = index + 1;
    else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') target = index - 1;
    else if (event.key === 'Home') target = 0;
    else if (event.key === 'End') target = total - 1;
    else if ((event.key === 'Enter' || event.key === ' ') && event.target === track) {
      event.preventDefault();
      if (!lowRes(index)) openZoom(index);
      return;
    }
    if (target === null) return;
    event.preventDefault();
    goTo(target);
    if (onThumb) thumbs[(target + total) % total]?.focus({ preventScroll: true });
  });

  // Soft fade on the rail edges only while more thumbnails are waiting there.
  const syncRailFade = () => {
    if (!rail) return;
    const strip = railIsStrip();
    const scrolled = strip ? rail.scrollLeft : rail.scrollTop;
    const more = strip ? rail.scrollWidth - rail.clientWidth : rail.scrollHeight - rail.clientHeight;
    const fade = strip ? '1.5rem' : '2.5rem';
    rail.style.setProperty('--pdp-fade-top', scrolled > 4 ? fade : '0px');
    rail.style.setProperty('--pdp-fade-bottom', more - scrolled > 4 ? fade : '0px');
  };
  rail?.addEventListener('scroll', syncRailFade, { passive: true });

  /* ── Buying from inside the viewers ──────────────────────────────────── */

  const mainCta = document.querySelector('[data-add-to-cart]');
  const buyState = () => ({
    canBuy: Boolean(mainCta && !mainCta.disabled),
    // The code beside the price ("USD") is its own element; the viewer's bar shows them together
    price: [document.querySelector('.pdp__price'), document.querySelector('[data-pdp-price-code]')]
      .map((element) => element?.textContent.trim()).filter(Boolean).join(' '),
    title: document.querySelector('.pdp__name')?.textContent.trim() || document.title
  });
  const buyFromViewer = (close) => {
    close();
    // The regular button runs the theme's normal add-to-bag flow.
    requestAnimationFrame(() => mainCta?.click());
  };
  // The viewers' buttons say what the page's button says: "In Your Bag" once this
  // unit is in the bag (assets/cart.js sets data-in-bag and fires bag:changed).
  const buyLabel = () => (mainCta?.hasAttribute('data-in-bag') ? 'In Your Bag · View' : 'Add to Bag');
  document.addEventListener('bag:changed', () => {
    document.querySelectorAll('.pdp-pswp__atc, .pdp-overview__atc').forEach((button) => { button.textContent = buyLabel(); });
  });

  const sizedSrc = (img, width) => {
    const candidates = (img.getAttribute('srcset') || img.dataset.srcset || '').split(',').map((part) => {
      const [url, size] = part.trim().split(/\s+/);
      return { url, width: parseInt(size, 10) || 0 };
    }).filter((c) => c.url).sort((a, b) => a.width - b.width);
    return (candidates.find((c) => c.width >= width) || candidates[candidates.length - 1] || {}).url || img.currentSrc || img.src;
  };

  /* ── Full-screen zoom: PhotoSwipe, loaded on first use ───────────────── */

  let photoSwipe = null;
  const loadPhotoSwipe = () => {
    if (photoSwipe) return photoSwipe;
    const css = root.dataset.pswpCss;
    if (css && !document.querySelector('link[data-pswp-css]')) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = css;
      link.dataset.pswpCss = '';
      document.head.append(link);
    }
    photoSwipe = import(new URL(root.dataset.pswpSrc, document.baseURI).href).then((module) => module.default);
    return photoSwipe;
  };

  const largest = (img) => {
    const candidates = (img.getAttribute('srcset') || img.dataset.srcset || '').split(',').map((part) => {
      const [url, size] = part.trim().split(/\s+/);
      return { url, width: parseInt(size, 10) || 0 };
    }).filter((c) => c.url);
    candidates.sort((a, b) => b.width - a.width);
    return candidates[0]?.url || img.currentSrc || img.src;
  };

  // Zoom uses the original upload (Liquid puts its URL and true size on the
  // slide), so magnification never goes past the photo's real resolution.
  const dataSource = () => slides.map((slide) => {
    const img = slide.querySelector('img');
    if (!img) return { html: '<div></div>' };
    const width = parseInt(slide.dataset.zoomWidth || img.getAttribute('width'), 10) || 1600;
    const height = parseInt(slide.dataset.zoomHeight || img.getAttribute('height'), 10) || width;
    // `element` is the slide: PhotoSwipe animates from the <img> inside it.
    const thumb = thumbs[slides.indexOf(slide)]?.querySelector('img');
    const msrc = img.currentSrc || img.getAttribute('src') || (thumb && (thumb.currentSrc || thumb.src)) || '';
    const src = slide.dataset.zoomSrc || largest(img);
    const ladder = img.getAttribute('srcset') || img.dataset.srcset || '';
    const srcset = ladder && !ladder.includes(`${width}w`) ? `${ladder}, ${src} ${width}w` : ladder;
    return { src, srcset: srcset || undefined, msrc, width, height, alt: img.alt, element: slide };
  });

  // The viewer wears the site's icons (Hugeicons, MIT), not PhotoSwipe's stock set.
  const icon = (path, size = 24) => `<svg class="pswp__icn" viewBox="0 0 24 24" width="${size}" height="${size}" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${path}</svg>`;
  const houseIcons = () => ({
    closeSVG: icon('<path d="M18 6L6.00081 17.9992M17.9992 18L6 6.00085"/>'),
    // The vertical bar is its own path: PhotoSwipe hides it when zoomed in (+ becomes −).
    zoomSVG: icon('<path d="M17 17L21 21"/><path d="M19 11C19 6.58172 15.4183 3 11 3C6.58172 3 3 6.58172 3 11C3 15.4183 6.58172 19 11 19C15.4183 19 19 15.4183 19 11Z"/><path d="M7.5 11L14.5 11"/><path class="pswp__zoom-icn-bar-v" d="M11 7.5V14.5"/>'),
    arrowPrevSVG: icon('<path d="M15 6C15 6 9.00001 10.4189 9 12C8.99999 13.5812 15 18 15 18"/>', 32),
    arrowNextSVG: icon('<path d="M9.00005 6C9.00005 6 15 10.4189 15 12C15 13.5812 9 18 9 18"/>', 32)
  });

  async function openZoom(start) {
    if (!root.dataset.pswpSrc) return;
    const PhotoSwipe = await loadPhotoSwipe();
    const pswp = new PhotoSwipe({
      dataSource: dataSource(),
      index: start,
      bgOpacity: 1,
      showHideAnimationType: reducedMotion.matches ? 'none' : 'zoom',
      initialZoomLevel: 'fit',
      // Real size (1:1) is the sharpest detail a photo has; never enlarge past it.
      secondaryZoomLevel: (zoom) => (desktop.matches ? Math.max(1, zoom.fit) : Math.min(Math.max(1, zoom.fit), zoom.fit * 2.5)),
      maxZoomLevel: (zoom) => Math.max(1, zoom.fit),
      wheelToZoom: true,
      // Room for the top bar and the thumbnails: a rail on the left on
      // desktop, a strip along the bottom on phones.
      paddingFn: (viewport) => {
        if (viewport.x >= 990) return total > 1 ? { top: 72, bottom: 24, left: 128, right: 72 } : { top: 72, bottom: 24, left: 72, right: 72 };
        return { top: 56, bottom: total > 1 ? 76 : 16, left: 0, right: 0 };
      },
      ...houseIcons(),
      closeTitle: 'Close',
      zoomTitle: 'Zoom',
      arrowPrevTitle: 'Previous photo',
      arrowNextTitle: 'Next photo',
      mainClass: 'pdp-pswp'
    });
    // Animate from the photo as drawn: object-fit contain can letterbox the
    // rare non-square photo inside the square stage.
    pswp.addFilter('thumbBounds', (bounds, itemData, i) => {
      if (i !== index || !bounds) return undefined;
      const img = slides[i]?.querySelector('img');
      if (!img) return bounds;
      const rect = img.getBoundingClientRect();
      // Phones: once the details sheet covers the pinned photo, fade instead
      // of flying into a photo nobody can see.
      const sheetTop = pdp?.querySelector('.pdp__info')?.getBoundingClientRect().top;
      if (!desktop.matches && sheetTop !== undefined && sheetTop < rect.top + rect.height / 2) return undefined;
      const nw = img.naturalWidth || itemData.w;
      const nh = img.naturalHeight || itemData.h;
      if (!nw || !nh) return bounds;
      const scale = Math.min(rect.width / nw, rect.height / nh);
      const w = nw * scale;
      const h = nh * scale;
      return { x: rect.left + (rect.width - w) / 2, y: rect.top + (rect.height - h) / 2, w };
    });
    pswp.on('uiRegister', () => {
      const { canBuy, price } = buyState();
      if (canBuy) {
        // No luxury house keeps the price and Add to Bag inside its viewer.
        pswp.ui.registerElement({
          name: 'ldj-buy',
          order: 40,
          isButton: false,
          appendTo: 'root',
          onInit: (el, viewer) => {
            el.classList.add('pdp-pswp__buy');
            el.innerHTML = `<span class="pdp-pswp__price"></span><button type="button" class="pdp-pswp__atc"></button>`;
            el.querySelector('.pdp-pswp__price').textContent = price;
            el.querySelector('.pdp-pswp__atc').textContent = buyLabel();
            el.querySelector('button').addEventListener('click', () => buyFromViewer(() => viewer.close()));
          }
        });
      }
      if (total > 1 && thumbs.length) {
        pswp.ui.registerElement({
          name: 'ldj-rail',
          order: 30,
          appendTo: 'root',
          isButton: false,
          onInit: (el, viewer) => {
            el.classList.add('pdp-pswp__rail');
            el.setAttribute('role', 'group');
            el.setAttribute('aria-label', 'All Photos');
            el.innerHTML = thumbs.map((thumb, i) => {
              const img = thumb.querySelector('img');
              const src = img ? (img.currentSrc || img.src) : '';
              return `<button type="button" class="pdp-pswp__thumb" aria-label="Show photo ${i + 1} of ${total}"><img src="${src}" alt="" width="100" height="100" loading="lazy" decoding="async"></button>`;
            }).join('');
            const railThumbs = Array.from(el.children);
            railThumbs.forEach((button, i) => button.addEventListener('click', () => viewer.goTo(i)));
            const sync = () => {
              railThumbs.forEach((button, i) => button.setAttribute('aria-current', String(i === viewer.currIndex)));
              const active = railThumbs[viewer.currIndex];
              if (!active) return;
              if (el.scrollWidth > el.clientWidth + 1) el.scrollTo({ left: active.offsetLeft - (el.clientWidth - active.offsetWidth) / 2, behavior: 'auto' });
              else el.scrollTo({ top: active.offsetTop - el.clientHeight / 2 + active.offsetHeight / 2, behavior: 'auto' });
            };
            viewer.on('change', sync);
            sync();
          }
        });
      }
    });
    pswp.on('afterInit', () => {
      pswp.element?.setAttribute('aria-modal', 'true');
      pswp.element?.setAttribute('aria-label', 'Product photo viewer');
    });
    // Phones: Back closes the viewer rather than leaving the page.
    if (coarse.matches && window.history?.pushState) {
      const onPop = () => pswp.close();
      pswp.on('afterInit', () => {
        // Opened from the grid: reuse its entry, so Back still takes one step
        if (history.state?.ldjOverview) history.replaceState({ ldjViewer: true }, '');
        else history.pushState({ ldjViewer: true }, '');
        window.addEventListener('popstate', onPop);
      });
      pswp.on('destroy', () => {
        window.removeEventListener('popstate', onPop);
        if (history.state?.ldjViewer) history.back();
      });
    }
    // Keyboard focus starts on Close, the first thing anyone looks for.
    // (PhotoSwipe focuses its own root during this event; go one frame later.)
    pswp.on('openingAnimationEnd', () => {
      requestAnimationFrame(() => pswp.element?.querySelector('.pswp__button--close')?.focus({ preventScroll: true }));
    });
    pswp.on('change', () => goTo(pswp.currIndex, false));
    pswp.init();
  }

  track.addEventListener('click', (event) => {
    const slide = event.target.closest('.pdp__slide');
    if (slide && !lowRes(slides.indexOf(slide))) openZoom(slides.indexOf(slide));
  });
  expand?.addEventListener('click', () => openZoom(index));

  /* ── "View all": every photo on one screen ──────────────────────────── */

  let overview = null;
  const buildOverview = () => {
    const { canBuy, price, title } = buyState();
    const dialog = document.createElement('dialog');
    dialog.className = 'pdp-overview';
    dialog.setAttribute('aria-label', `All ${total} Photos`);
    dialog.innerHTML = `
      <div class="pdp-overview__bar">
        <p class="pdp-overview__title"><span class="pdp-overview__name"></span><span class="pdp-overview__count">${total} photos</span></p>
        ${canBuy ? '<span class="pdp-overview__price"></span><button class="button pdp-overview__atc" type="button"></button>' : ''}
        <button class="pdp-overview__close" type="button" aria-label="Close all photos">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M18 6L6.00081 17.9992M17.9992 18L6 6.00085"/></svg>
        </button>
      </div>
      <ul class="pdp-overview__grid" role="list"></ul>`;
    dialog.querySelector('.pdp-overview__name').textContent = title;
    if (canBuy) {
      dialog.querySelector('.pdp-overview__price').textContent = price;
      dialog.querySelector('.pdp-overview__atc').textContent = buyLabel();
    }
    const grid = dialog.querySelector('.pdp-overview__grid');
    slides.forEach((slide, i) => {
      const img = slide.querySelector('img');
      if (!img) return;
      const item = document.createElement('li');
      item.innerHTML = `<button type="button" class="pdp-overview__photo" aria-label="Show photo ${i + 1} of ${total}"><img alt="" loading="lazy" decoding="async" width="600" height="600"><span aria-hidden="true">${i + 1}</span></button>`;
      const tile = item.querySelector('img');
      const base = sizedSrc(img, 600);
      if (/[?&]width=\d+/.test(base)) {
        tile.srcset = [200, 300, 400, 600].map((w) => `${base.replace(/([?&]width=)\d+/, `$1${w}`)} ${w}w`).join(', ');
        tile.sizes = '(min-width: 990px) 12rem, 34vw';
      }
      tile.src = base;
      item.querySelector('button').addEventListener('click', () => {
        // Phones: the viewer takes over the grid's history entry (no Back race)
        if (!desktop.matches) dialog.dataset.handoff = '1';
        dialog.close();
        goTo(i, false);
        if (desktop.matches) track.focus({ preventScroll: true });
        else openZoom(i);
      });
      grid.append(item);
    });
    dialog.querySelector('.pdp-overview__close').addEventListener('click', () => dialog.close());
    dialog.querySelector('.pdp-overview__atc')?.addEventListener('click', () => buyFromViewer(() => dialog.close()));
    dialog.addEventListener('click', (event) => { if (event.target === dialog) dialog.close(); });
    document.body.append(dialog);
    return dialog;
  };

  const openOverview = () => {
    if (!overview) {
      overview = buildOverview();
      if (coarse.matches && window.history?.pushState) {
        const onPop = () => { if (overview.open) overview.close(); };
        overview.addEventListener('close', () => {
          window.removeEventListener('popstate', onPop);
          const handoff = overview.dataset.handoff;
          delete overview.dataset.handoff;
          if (!handoff && history.state?.ldjOverview) history.back();
        });
        overview.addEventListener('ldj-open', () => {
          history.pushState({ ldjOverview: true }, '');
          window.addEventListener('popstate', onPop);
        });
      }
    }
    overview.showModal();
    overview.dispatchEvent(new Event('ldj-open'));
    overview.querySelectorAll('.pdp-overview__photo')[index]?.focus();
  };
  overviewButton?.addEventListener('click', openOverview);
  // "See all N photos" in the condition report opens the same grid
  document.addEventListener('click', (event) => {
    if (event.target.closest?.('[data-pdp-overview-open]')) openOverview();
  });
  // Fetch the viewer's code as soon as the shopper shows intent.
  root.addEventListener('pointerenter', () => { if (desktop.matches) loadPhotoSwipe(); }, { once: true });
  root.addEventListener('touchstart', () => loadPhotoSwipe(), { once: true, passive: true });

  /* ── Start ──────────────────────────────────────────────────────────── */

  const init = () => {
    const help = document.getElementById(track.getAttribute('aria-describedby') || '');
    if (help && coarse.matches) help.textContent = 'Swipe to browse the photos, and tap one to open it full screen.';
    measure();
    syncRailFade();
    setIndex(0);
    ready = true;
    // The next photo is usually wanted within seconds: fetch it once the
    // page has loaded and the browser is idle, never alongside photo 1.
    // Data savers and slow links wait for the shopper to ask.
    const connection = navigator.connection;
    if (connection && (connection.saveData || /(^|-)2g$|^3g$/.test(connection.effectiveType || ''))) return;
    // Neighbours of the photo in view: photo 2 on desktop; on phones the
    // loop starts one slide in, so this is the real photo 2 and the last one.
    const warmNear = () => {
      const i = Math.round(track.scrollLeft / (track.clientWidth || 1));
      warm(i + 1);
      if (i > 0) warm(i - 1);
    };
    const idle = () => (window.requestIdleCallback || ((fn) => setTimeout(fn, 1200)))(warmNear, { timeout: 4000 });
    if (document.readyState === 'complete') idle();
    else window.addEventListener('load', idle, { once: true });
  };

  let resizeTimer;
  window.addEventListener('resize', () => {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(() => {
      measure();
      syncRailFade();
      root.classList.toggle('is-lowres', lowRes(index));
      if (desktop.matches) track.scrollTo({ left: index * track.clientWidth, behavior: 'auto' });
    }, 120);
  });
  document.fonts?.ready.then(measure);
  init();
})();

/* ── Certificate of Authenticity: its two scripts load on the first open ────────────── */
(() => {
  const button = document.querySelector('[data-pdp-pass-open][data-pdp-pass-scripts]');
  if (!button) return;
  let loading = null;
  const load = (src) => new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = src;
    script.onload = resolve;
    script.onerror = reject;
    document.head.append(script);
  });
  // Capture phase: runs before pdp-passport.js's own listener once it exists.
  button.addEventListener('click', (event) => {
    if (button.dataset.pdpPassReady) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (loading) return;
    button.setAttribute('aria-busy', 'true');
    const [qr, passport] = button.dataset.pdpPassScripts.split(' ');
    loading = load(qr).then(() => load(passport)).then(() => {
      button.dataset.pdpPassReady = 'true';
      button.removeAttribute('aria-busy');
      button.click();
    }).catch(() => {
      loading = null;
      button.removeAttribute('aria-busy');
    });
  }, true);
})();

/* ── Dialogs (condition guide, certificate, private viewing, authenticity): focus moves in, stays in,
      and returns to whatever opened them ───────────────────────────────── */
(() => {
  const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  const watch = (dialog) => {
    let opener = null;
    const focusables = () => Array.from(dialog.querySelectorAll(FOCUSABLE))
      .filter((element) => element.getClientRects().length > 0);

    const onKeydown = (event) => {
      if (event.key !== 'Tab') return;
      const items = focusables();
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (!dialog.contains(document.activeElement)) {
        event.preventDefault();
        first.focus();
      } else if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    new MutationObserver(() => {
      const open = dialog.getAttribute('aria-hidden') === 'false';
      if (open && dialog.dataset.pdpTrap === undefined) {
        dialog.dataset.pdpTrap = '';
        opener = document.activeElement;
        document.addEventListener('keydown', onKeydown, true);
        // Focus the dialog's panel (tabindex="-1"): screen readers announce the dialog and Tab
        // moves on to its controls, without a focus ring drawn on the close button at open.
        requestAnimationFrame(() => {
          const panel = dialog.querySelector('[data-pdp-dialog-panel]');
          const close = dialog.querySelector('button[data-pdp-cond-close], button[data-pdp-pass-close], button[data-pdp-viewing-close], button[data-pdp-auth-close]');
          (panel || close || focusables()[0])?.focus({ preventScroll: true });
        });
      } else if (!open && dialog.dataset.pdpTrap !== undefined) {
        delete dialog.dataset.pdpTrap;
        document.removeEventListener('keydown', onKeydown, true);
        if (opener && document.contains(opener)) opener.focus({ preventScroll: true });
      }
    }).observe(dialog, { attributes: true, attributeFilter: ['aria-hidden'] });
  };

  document.querySelectorAll('[data-pdp-cond], [data-pdp-pass], [data-pdp-viewing], [data-pdp-auth]').forEach(watch);
})();

/* Terms priced in the shop currency (CAD), such as the layaway deposit tier
   and Splitit's limit, are rendered for CAD. For other currencies, re-check
   them against the unit price converted back with Shopify's own rate. */
(() => {
  const block = document.querySelector('[data-unit-price]');
  const rate = Number(window.Shopify?.currency?.rate);
  if (!block || !rate || rate === 1) return;
  const base = Number(block.dataset.unitPrice) / rate;
  block.querySelectorAll('[data-base-below], [data-base-from]').forEach((el) => {
    const below = el.dataset.baseBelow;
    const from = el.dataset.baseFrom;
    el.hidden = !((below === undefined || base < Number(below)) && (from === undefined || base >= Number(from)));
  });
})();

/* ── Condition report, Details and the promise rows open and close with an
      eased height and a soft fade, in every browser (native <details> just
      snaps). Reduced motion keeps the instant toggle. ───────────────────── */
(() => {
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  const EASE = 'cubic-bezier(0.22, 1, 0.36, 1)';

  document.querySelectorAll('.pdp details').forEach((details) => {
    const summary = details.querySelector(':scope > summary');
    if (!summary) return;
    let anim = null;

    const closedHeight = () => {
      const cs = getComputedStyle(details);
      return summary.offsetHeight
        + parseFloat(cs.paddingTop) + parseFloat(cs.paddingBottom)
        + parseFloat(cs.borderTopWidth) + parseFloat(cs.borderBottomWidth);
    };

    summary.addEventListener('click', (event) => {
      if (reduce.matches) return;
      event.preventDefault();
      // Start from wherever it is now (a click mid-animation reverses it).
      const from = details.offsetHeight;
      anim?.cancel();
      const opening = !details.open || details.classList.contains('is-closing');
      details.classList.toggle('is-closing', !opening);
      if (opening) details.open = true;
      const to = opening ? details.offsetHeight : closedHeight();
      const duration = Math.round(Math.min(460, Math.max(240, Math.abs(to - from) * 0.9)));

      details.style.overflow = 'hidden';
      anim = details.animate({ height: [`${from}px`, `${to}px`] }, { duration, easing: EASE });
      summary.nextElementSibling?.animate(
        { opacity: opening ? [0, 1] : [1, 0], transform: opening ? ['translateY(-4px)', 'none'] : ['none', 'translateY(-4px)'] },
        { duration: Math.round(duration * 0.8), easing: 'ease' }
      );
      const settle = () => {
        details.style.overflow = '';
        anim = null;
      };
      anim.onfinish = () => {
        if (!opening) {
          details.open = false;
          details.classList.remove('is-closing');
        }
        settle();
      };
      anim.oncancel = settle;
    });
  });
})();
